<template>
  <div class="comp__table-wrapper">
    <!-- Header (acts like thead) -->
    <div class="comp__table-header" ref="headerScroll">
      <div class="comp__table-product" v-for="product in products" :key="product.id">
        <div class="comp__table-product-image">
          <ImageField class="comp__table-product-image" :key="product.Color" :obj="{'url': getProductFirstOptions(product)[product.Color].featured_image || product.featured_image,'alt':'Feature Image', 'width':225,'height':297}" />
          <button class="comp__table-product-remove-mob" @click="emit('on-remove-item', product)" aria-label="Remove product from compare chart">
            <svg class="comp__table-remove-icon">
              <use xlink:href="#close"></use>
            </svg>
          </button>
        </div>
        <div class="comp__table-product-right">
          <div class="comp__table-product-inner-info">
            <span class="comp__table-product-title">{{ product.title }}</span>
            <ProductPrice
              :key="product.Color"
              :product="product"
              :selectedFirstOption="getProductFirstOptions(product)[product.Color]"
            />
          </div>
          <div v-if="!isProductATCInViewport" class="comp__table-product-ctas">
            <button @click="redirectToBottom" class="comp__table-product-atc" tabindex="0">Add to Cart</button>
          </div>
        </div>
      </div>
    </div>
  
    <!-- Body (acts like tbody) -->
    <div class="comp__table-body" ref="bodyScroll">
      <div class="comp__table-section" v-for="headingData in uniqueHeadings" :key="headingData.heading">
        <div class="comp__table-sticky-heading">
          <span>{{ headingData.heading }}</span>
        </div> 
        <div class="comp__table-row" v-if="headingData.key.toLowerCase() != 'buying options'">
          <div class="comp__table-cell" v-for="(data, cIndex) in comparisonTable" :key="cIndex">
            <div v-if="headingData.key.toLowerCase() === 'ratings' && data[headingData.key][0].value.toLowerCase() === 'true'">
              <div class="yotpo-widget-instance" data-yotpo-instance-id="498880" :data-yotpo-product-id="data[headingData.key][0].productId"></div>
            </div>
            <div v-else-if="headingData.key.toLowerCase() === 'fabric' && data[headingData.key][0].value.toLowerCase() === 'true'">
              <div v-html="data[headingData.key][0].fabricDetails"></div>
            </div>
            <div v-else-if="headingData.key.toLowerCase() === 'measurements' && data[headingData.key][0].value.toLowerCase() === 'true'">
              <div v-html="data[headingData.key][0].measurementsDetails"></div>
            </div>
            <div v-else v-for="item in data[headingData.key]" :key="item.value">
              <div v-if="item.icon" class="comp__table-td-icon">
                <ImageField :obj="{'url': item.icon,'alt':'Icon Image', 'width':240,'height':240}" />
                <span>{{ item.value }}</span>
              </div>
              <div v-else>{{ item.value }}</div>
            </div>
          </div>
        </div>
  
        <div class="comp__table-row" v-else>
          <div id="buyingOptions" class="comp__table-cell" v-for="(product, pIndex) in products" :key="product.id">
            <div class="comp__table-product-options">
              <div class="comp__table-buying-options">
                <div v-for="(option, optionIndex) in product.options" :key="optionIndex" class="comp__table-buying-option">
                  <span class="buying__option-label">{{ option.name }}</span>
                  <CustomSelect
                    v-if="Boolean(option.values.length) && optionIndex === 0"
                    :options="getProductFirstOptions(product)"
                    :callbackProps="{ key: option.name, pIndex }"
                    :placeholder="`Select ${option.name}`"
                    :selectedValueProp="product[product.options[0].name]"
                    :enableBodyOverflow="false"
                    :enablePrefixOnOption="false"
                    :enableSuffixOnOption="false"
                    :isColorOption="option.name ==='Color' ? true : false"
                    :isSizeOption="option.name ==='Size' ? true : false"
                    :onDropdownSelect="onOptionChange"
                  />
                  <CustomSelect
                    v-else-if="Boolean(option.values.length)"
                    :options="getProductOptions(product, optionIndex)"
                    optionPrefix=""
                    :callbackProps="{ key: option.name, pIndex }"
                    :placeholder="`Select ${option.name}`"
                    :enableBodyOverflow="false"
                    :enablePrefixOnOption="false"
                    :selectedValueProp="product[product.options[optionIndex].name]"
                    :enableSuffixOnOption="false"
                    :isSizeOption="option.name ==='Size' ? true : false"
                    :onDropdownSelect="onOptionChange"
                  />
                </div>
              </div>
              <div ref="productATCs" class="comp__table-atc-ctas">
                <ProductATC
                  :key="product"
                  :product="product"
                  :productForm="getProductForm(product)"
                  :selectedVariant="getProductForm(product).selectedVariant"
                  :selectedFirstOption="getProductFirstOptions(product)[getProductForm(product)[product.options[0].name]]"
                />
                <a :href="`/products/${product.handle}?color=${product.Color}`" tabindex="0" class="comp__table-view-product-cta">View Product Details</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>   
</template>

<script setup>
  import { ref, toRefs, onMounted, nextTick } from 'vue';
  import ImageField from '../image.vue';
  import ProductPrice from '../../pdp-first-fold/components/product-price.vue';
  import ProductATC from '../../pdp-first-fold/components/product-atc.vue';
  import CustomSelect from '../custom-select.vue';

  const props = defineProps({
    products: {
      type: Object,
      required: true,
    },

    uniqueHeadings: {
      type: Object,
      required: true,
    },

    comparisonTable: {
      type: Object,
      required: true,
    }
  })

  const { products, uniqueHeadings, comparisonTable } = toRefs(props)
  const emit = defineEmits(['on-remove-item'])
  const productATCs = ref(null);
  const headerScroll = ref(null);
  const bodyScroll = ref(null);
  const isProductATCInViewport = ref(false);

  onMounted(() => {
    window.refreshYotpoData();

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        isProductATCInViewport.value = entry.isIntersecting;
      });
    });

    nextTick(() => {
      if (productATCs.value) {
        observer.observe(productATCs.value[0]);
      }

      // Sync the horizontal scrolling
      bodyScroll.value.addEventListener('scroll', () => {
        headerScroll.value.scrollLeft = bodyScroll.value.scrollLeft;
      });

      // Sync the vertical scrolling
      headerScroll.value.addEventListener('scroll', () => {
        bodyScroll.value.scrollLeft = headerScroll.value.scrollLeft;
      });
    });
  })

  const getProductFirstOptions = (product) => {
    const { variants, tags, available, images, options } = product;
    const firstOption = options[0]?.name;

    // Group variants by their first option (e.g., Color)
    const firstLevelOptions = variants.reduce((acc, variant) => {
      const option = variant.option1;
      if (!acc[option]) {
        acc[option] = { items: [], available: false, name: option };
      }
      acc[option].items.push(variant);
      if (variant.available) acc[option].available = true;
      return acc;
    }, {});

    // Process the grouped variants
    Object.entries(firstLevelOptions).forEach(([key, value]) => {
      const isAvailable = value.available;
      const bisData = tags.some(tag => tag.toLowerCase().includes(`bis: ${key.toLowerCase()}`));

      // Check if the variant is available or has BIS data or is globally unavailable
      if (isAvailable || bisData || !available) {
        const imagePattern = new RegExp(`^\\s*${key.toLowerCase()}\\s*swatch\\s*$`);
        firstOption === 'Color' && (value.image = images.filter(image => {
          const alt = image.alt.toLowerCase()
          return alt && imagePattern.test(alt)
        })[0].src)
        firstOption === 'Color' && (value.featured_image = value.items[0].featured_image);
        value.price = Number(value.items[0].price/100)
        value.compare_at_price = Number(value.items[0].compare_at_price/100)

        if(bisData.length){
          value.bis = true;
          value.bisDate = bisData[0].split('[')[1].replace(']','');
        }
      } else {
        delete firstLevelOptions[key];
      }
    });

    return firstLevelOptions
  }

  const getProductOptions = (product, optionIndex) => {
    const firstOptions = getProductFirstOptions(product);
    const firstOptionName = product.options[0].name;
    const option = 'option' + `${optionIndex + 1}`;

    // Use Set to get unique values and then convert back to an array
    return [...new Set(firstOptions[product[firstOptionName]].items.map(item => item[option]))];
  }
  
  const getProductForm = (product) => {
    return {
      Color: product.Color,
      Size: product.Size || null,
      Inseam: product.Inseam || null,
      Outseam: product.Outseam || null,
      selectedVariant: product.selectedVariant || null
    }
  }
  
  const onOptionChange = (value,inputProps) => {
    const { key, pIndex } = inputProps;

    const product = products.value[pIndex];
    product[key] = value;

    // Setting  Variant Value
    const productForm = getProductForm(product);
    const renderFirstOptions = getProductFirstOptions(product);
    const firstOption = product.options[0].name;
    const variantValue = product.options.map(option => productForm[option.name]).join(' / ');
    const { items } = renderFirstOptions[productForm[firstOption]];
    const variant = items.find(item => item.value === variantValue);
    product['selectedVariant'] = variant || null;

    // if(key != 'Color' && key != 'Title'){
    //   updateColorOOS(product, value,index,length)
    // }
  }

  const redirectToBottom = () => {
    document.querySelector('#buyingOptions').scrollIntoView({
      behavior: 'smooth'
    });
  }
</script>